import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

// Error function approximation (Abramowitz & Stegun, maximum error 2.5×10⁻⁵)
function erf(x: number): number {
    if (x < 0) return -erf(-x);
    const t = 1 / (1 + 0.47047 * x);
    const poly = t * (0.3480242 + t * (-0.0958798 + t * 0.7478556));
    return 1 - poly * Math.exp(-(x * x));
}

// More accurate erf for large x
function erfAccurate(x: number): number {
    if (!isFinite(x)) return x > 0 ? 1 : -1;
    if (x < 0) return -erfAccurate(-x);
    // Use built-in approximation via series if available, otherwise A&S 7.1.26
    // Horner form with A&S constants (max error ~1.5e-7)
    const t = 1 / (1 + 0.3275911 * x);
    const p = t * (0.254829592
        + t * (-0.284496736
            + t * (1.421413741
                + t * (-1.453152027
                    + t * 1.061405429))));
    return 1 - p * Math.exp(-(x * x));
}

interface Complex {
    re: number;
    im: number;
    suffix: string;
}

/**
 * Parse an Excel complex number string into its real/imaginary parts.
 * Handles: "3+4i", "3-4i", "3+4j", "3", "4i", "-2i", "i", "-i", "3j", etc.
 */
function parseComplex(str: string): Complex {
    const s = toStr(str).trim();
    if (s === '') return { re: 0, im: 0, suffix: 'i' };

    // Detect suffix character
    const lastChar = s[s.length - 1];
    const suffix = (lastChar === 'i' || lastChar === 'j') ? lastChar : 'i';
    const isComplex = lastChar === 'i' || lastChar === 'j';

    if (!isComplex) {
        // Pure real
        const re = parseFloat(s);
        if (isNaN(re)) throw new FormulaError(FormulaErrorType.VALUE, `COMPLEX: cannot parse "${s}"`);
        return { re, im: 0, suffix };
    }

    // Strip the suffix character
    const body = s.slice(0, -1);

    if (body === '' || body === '+') return { re: 0, im: 1, suffix };
    if (body === '-') return { re: 0, im: -1, suffix };

    // Try pure imaginary (no real part): body has no +/- except possibly a leading sign
    // Find the last + or - that is NOT at position 0 (i.e. not a leading sign)
    // and NOT right after 'e' or 'E' (scientific notation)
    let splitIdx = -1;
    for (let i = body.length - 1; i > 0; i--) {
        const ch = body[i];
        if ((ch === '+' || ch === '-') && body[i - 1] !== 'e' && body[i - 1] !== 'E') {
            splitIdx = i;
            break;
        }
    }

    if (splitIdx === -1) {
        // Pure imaginary
        const im = body === '' ? 1 : (body === '+' ? 1 : (body === '-' ? -1 : parseFloat(body)));
        if (isNaN(im)) throw new FormulaError(FormulaErrorType.VALUE, `COMPLEX: cannot parse "${s}"`);
        return { re: 0, im, suffix };
    }

    const rePart = body.slice(0, splitIdx);
    const imPart = body.slice(splitIdx);

    const re = parseFloat(rePart);
    let im: number;
    if (imPart === '+') {
        im = 1;
    } else if (imPart === '-') {
        im = -1;
    } else {
        im = parseFloat(imPart);
    }

    if (isNaN(re) || isNaN(im)) {
        throw new FormulaError(FormulaErrorType.VALUE, `COMPLEX: cannot parse "${s}"`);
    }

    return { re, im, suffix };
}

/**
 * Format a complex number back into an Excel-style string.
 * Uses the provided suffix ('i' or 'j'); defaults to 'i'.
 */
function formatComplex(re: number, im: number, suffix = 'i'): string {
    const sfx = suffix === 'j' ? 'j' : 'i';
    re = smartRound(re);
    im = smartRound(im);

    if (im === 0) return String(re);
    if (re === 0) {
        if (im === 1) return sfx;
        if (im === -1) return `-${sfx}`;
        return `${im}${sfx}`;
    }
    if (im === 1) return `${re}+${sfx}`;
    if (im === -1) return `${re}-${sfx}`;
    if (im < 0) return `${re}${im}${sfx}`;
    return `${re}+${im}${sfx}`;
}

/** Multiply two complex numbers */
function complexMul(a: Complex, b: Complex): Complex {
    return {
        re: a.re * b.re - a.im * b.im,
        im: a.re * b.im + a.im * b.re,
        suffix: a.suffix,
    };
}

/** Apply padding (places) to a base-converted string */
function applyPlaces(str: string, places: number | undefined): string {
    if (places === undefined) return str;
    const p = Math.floor(places);
    if (p < 0) throw new FormulaError(FormulaErrorType.NUM, 'Places must be non-negative');
    if (str.length > p) throw new FormulaError(FormulaErrorType.NUM, 'Result longer than places');
    return str.padStart(p, '0');
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerEngineeringFunctions(registry: FunctionRegistry): void {
    // --- BIN/OCT/HEX/DEC conversions ---

    registry.register('BIN2DEC', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[01]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'BIN2DEC: invalid binary string');
        // Excel treats 10-digit binary as two's complement (bit 9 = sign)
        if (s.length === 10 && s[0] === '1') {
            return parseInt(s, 2) - 1024;
        }
        return parseInt(s, 2);
    });

    registry.register('BIN2HEX', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[01]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'BIN2HEX: invalid binary string');
        let dec: number;
        if (s.length === 10 && s[0] === '1') {
            dec = parseInt(s, 2) - 1024;
        } else {
            dec = parseInt(s, 2);
        }
        // Excel uses 10-digit two's complement hex for negatives
        const hex = dec < 0
            ? (dec + 0x10000000000).toString(16).toUpperCase()
            : dec.toString(16).toUpperCase();
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(hex, places);
    });

    registry.register('BIN2OCT', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[01]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'BIN2OCT: invalid binary string');
        let dec: number;
        if (s.length === 10 && s[0] === '1') {
            dec = parseInt(s, 2) - 1024;
        } else {
            dec = parseInt(s, 2);
        }
        const oct = dec < 0
            ? (dec + 0x10000000000).toString(8).toUpperCase()
            : dec.toString(8);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(oct, places);
    });

    registry.register('DEC2BIN', (...args: FormulaValue[]) => {
        const n = Math.trunc(toNum(args[0]));
        if (n < -512 || n > 511) throw new FormulaError(FormulaErrorType.NUM, 'DEC2BIN: number out of range [-512, 511]');
        const bin = n < 0
            ? (n + 1024).toString(2)
            : n.toString(2);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(bin, places);
    });

    registry.register('DEC2HEX', (...args: FormulaValue[]) => {
        const n = Math.trunc(toNum(args[0]));
        if (n < -549755813888 || n > 549755813887) {
            throw new FormulaError(FormulaErrorType.NUM, 'DEC2HEX: number out of range');
        }
        const hex = n < 0
            ? (n + 0x10000000000).toString(16).toUpperCase()
            : n.toString(16).toUpperCase();
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(hex, places);
    });

    registry.register('DEC2OCT', (...args: FormulaValue[]) => {
        const n = Math.trunc(toNum(args[0]));
        if (n < -536870912 || n > 536870911) {
            throw new FormulaError(FormulaErrorType.NUM, 'DEC2OCT: number out of range');
        }
        const oct = n < 0
            ? (n + 0x10000000000).toString(8)
            : n.toString(8);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(oct, places);
    });

    registry.register('HEX2BIN', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim().toUpperCase();
        if (!/^[0-9A-F]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'HEX2BIN: invalid hex string');
        let dec = parseInt(s, 16);
        // Two's complement: top bit of 40 bits
        if (dec > 549755813887) dec -= 0x10000000000;
        if (dec < -512 || dec > 511) throw new FormulaError(FormulaErrorType.NUM, 'HEX2BIN: number out of binary range');
        const bin = dec < 0 ? (dec + 1024).toString(2) : dec.toString(2);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(bin, places);
    });

    registry.register('HEX2DEC', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim().toUpperCase();
        if (!/^[0-9A-F]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'HEX2DEC: invalid hex string');
        let dec = parseInt(s, 16);
        if (dec > 549755813887) dec -= 0x10000000000;
        return dec;
    });

    registry.register('HEX2OCT', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim().toUpperCase();
        if (!/^[0-9A-F]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'HEX2OCT: invalid hex string');
        let dec = parseInt(s, 16);
        if (dec > 549755813887) dec -= 0x10000000000;
        if (dec < -536870912 || dec > 536870911) throw new FormulaError(FormulaErrorType.NUM, 'HEX2OCT: number out of octal range');
        const oct = dec < 0 ? (dec + 0x10000000000).toString(8) : dec.toString(8);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(oct, places);
    });

    registry.register('OCT2BIN', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[0-7]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'OCT2BIN: invalid octal string');
        let dec = parseInt(s, 8);
        if (dec > 536870911) dec -= 0x10000000000;
        if (dec < -512 || dec > 511) throw new FormulaError(FormulaErrorType.NUM, 'OCT2BIN: number out of binary range');
        const bin = dec < 0 ? (dec + 1024).toString(2) : dec.toString(2);
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(bin, places);
    });

    registry.register('OCT2DEC', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[0-7]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'OCT2DEC: invalid octal string');
        let dec = parseInt(s, 8);
        if (dec > 536870911) dec -= 0x10000000000;
        return dec;
    });

    registry.register('OCT2HEX', (...args: FormulaValue[]) => {
        const s = toStr(args[0]).trim();
        if (!/^[0-7]{1,10}$/.test(s)) throw new FormulaError(FormulaErrorType.NUM, 'OCT2HEX: invalid octal string');
        let dec = parseInt(s, 8);
        if (dec > 536870911) dec -= 0x10000000000;
        const hex = dec < 0 ? (dec + 0x10000000000).toString(16).toUpperCase() : dec.toString(16).toUpperCase();
        const places = args[1] !== undefined ? toNum(args[1]) : undefined;
        return applyPlaces(hex, places);
    });

    // --- Bitwise operations ---

    registry.register('BITAND', (...args: FormulaValue[]) => {
        const a = Math.trunc(toNum(args[0]));
        const b = Math.trunc(toNum(args[1]));
        if (a < 0 || b < 0) throw new FormulaError(FormulaErrorType.NUM, 'BITAND: arguments must be non-negative');
        // Use BigInt to avoid 32-bit sign extension
        return Number(BigInt(a) & BigInt(b));
    });

    registry.register('BITOR', (...args: FormulaValue[]) => {
        const a = Math.trunc(toNum(args[0]));
        const b = Math.trunc(toNum(args[1]));
        if (a < 0 || b < 0) throw new FormulaError(FormulaErrorType.NUM, 'BITOR: arguments must be non-negative');
        return Number(BigInt(a) | BigInt(b));
    });

    registry.register('BITXOR', (...args: FormulaValue[]) => {
        const a = Math.trunc(toNum(args[0]));
        const b = Math.trunc(toNum(args[1]));
        if (a < 0 || b < 0) throw new FormulaError(FormulaErrorType.NUM, 'BITXOR: arguments must be non-negative');
        return Number(BigInt(a) ^ BigInt(b));
    });

    registry.register('BITLSHIFT', (...args: FormulaValue[]) => {
        const n = Math.trunc(toNum(args[0]));
        const shift = Math.trunc(toNum(args[1]));
        if (n < 0) throw new FormulaError(FormulaErrorType.NUM, 'BITLSHIFT: number must be non-negative');
        if (Math.abs(shift) > 53) throw new FormulaError(FormulaErrorType.NUM, 'BITLSHIFT: shift amount out of range');
        return shift >= 0 ? Number(BigInt(n) << BigInt(shift)) : Number(BigInt(n) >> BigInt(-shift));
    });

    registry.register('BITRSHIFT', (...args: FormulaValue[]) => {
        const n = Math.trunc(toNum(args[0]));
        const shift = Math.trunc(toNum(args[1]));
        if (n < 0) throw new FormulaError(FormulaErrorType.NUM, 'BITRSHIFT: number must be non-negative');
        if (Math.abs(shift) > 53) throw new FormulaError(FormulaErrorType.NUM, 'BITRSHIFT: shift amount out of range');
        return shift >= 0 ? Number(BigInt(n) >> BigInt(shift)) : Number(BigInt(n) << BigInt(-shift));
    });

    // --- COMPLEX constructor ---

    registry.register('COMPLEX', (...args: FormulaValue[]) => {
        const re = toNum(args[0]);
        const im = toNum(args[1]);
        const sfx = args[2] !== undefined ? toStr(args[2]) : 'i';
        if (sfx !== 'i' && sfx !== 'j') {
            throw new FormulaError(FormulaErrorType.VALUE, 'COMPLEX: suffix must be "i" or "j"');
        }
        return formatComplex(re, im, sfx);
    });

    // --- DELTA ---

    registry.register('DELTA', (...args: FormulaValue[]) => {
        const a = toNum(args[0]);
        const b = args[1] !== undefined ? toNum(args[1]) : 0;
        return a === b ? 1 : 0;
    });

    // --- ERF / ERFC ---

    registry.register('ERF', (...args: FormulaValue[]) => {
        const lower = toNum(args[0]);
        if (args[1] !== undefined) {
            const upper = toNum(args[1]);
            return erfAccurate(upper) - erfAccurate(lower);
        }
        return erfAccurate(lower);
    });

    registry.register('ERFC', (...args: FormulaValue[]) => {
        const x = toNum(args[0]);
        return 1 - erfAccurate(x);
    });

    // --- Complex number functions ---

    registry.register('IMABS', (...args: FormulaValue[]) => {
        const { re, im } = parseComplex(toStr(args[0]));
        return Math.sqrt(re * re + im * im);
    });

    registry.register('IMAGINARY', (...args: FormulaValue[]) => {
        return parseComplex(toStr(args[0])).im;
    });

    registry.register('IMREAL', (...args: FormulaValue[]) => {
        return parseComplex(toStr(args[0])).re;
    });

    registry.register('IMARGUMENT', (...args: FormulaValue[]) => {
        const { re, im } = parseComplex(toStr(args[0]));
        if (re === 0 && im === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMARGUMENT: undefined for zero');
        return Math.atan2(im, re);
    });

    registry.register('IMCONJUGATE', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        return formatComplex(re, -im, suffix);
    });

    registry.register('IMCOS', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // cos(a+bi) = cos(a)*cosh(b) - i*sin(a)*sinh(b)
        return formatComplex(
            Math.cos(re) * Math.cosh(im),
            -Math.sin(re) * Math.sinh(im),
            suffix,
        );
    });

    registry.register('IMCOSH', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // cosh(a+bi) = cosh(a)*cos(b) + i*sinh(a)*sin(b)
        return formatComplex(
            Math.cosh(re) * Math.cos(im),
            Math.sinh(re) * Math.sin(im),
            suffix,
        );
    });

    registry.register('IMCOT', (...args: FormulaValue[]) => {
        // cot(z) = cos(z)/sin(z)
        const z = parseComplex(toStr(args[0]));
        const { re, im, suffix } = z;
        const sinRe = Math.sin(re) * Math.cosh(im);
        const sinIm = Math.cos(re) * Math.sinh(im);
        const cosRe = Math.cos(re) * Math.cosh(im);
        const cosIm = -Math.sin(re) * Math.sinh(im);
        const denom = sinRe * sinRe + sinIm * sinIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMCOT: undefined');
        return formatComplex(
            (cosRe * sinRe + cosIm * sinIm) / denom,
            (cosIm * sinRe - cosRe * sinIm) / denom,
            suffix,
        );
    });

    registry.register('IMCSC', (...args: FormulaValue[]) => {
        // csc(z) = 1/sin(z)
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const sinRe = Math.sin(re) * Math.cosh(im);
        const sinIm = Math.cos(re) * Math.sinh(im);
        const denom = sinRe * sinRe + sinIm * sinIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMCSC: undefined');
        return formatComplex(sinRe / denom, -sinIm / denom, suffix);
    });

    registry.register('IMCSCH', (...args: FormulaValue[]) => {
        // csch(z) = 1/sinh(z)
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const sinhRe = Math.sinh(re) * Math.cos(im);
        const sinhIm = Math.cosh(re) * Math.sin(im);
        const denom = sinhRe * sinhRe + sinhIm * sinhIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMCSCH: undefined');
        return formatComplex(sinhRe / denom, -sinhIm / denom, suffix);
    });

    registry.register('IMDIV', (...args: FormulaValue[]) => {
        const a = parseComplex(toStr(args[0]));
        const b = parseComplex(toStr(args[1]));
        const denom = b.re * b.re + b.im * b.im;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMDIV: division by zero');
        return formatComplex(
            (a.re * b.re + a.im * b.im) / denom,
            (a.im * b.re - a.re * b.im) / denom,
            a.suffix,
        );
    });

    registry.register('IMEXP', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // e^(a+bi) = e^a*(cos(b) + i*sin(b))
        const ea = Math.exp(re);
        return formatComplex(ea * Math.cos(im), ea * Math.sin(im), suffix);
    });

    registry.register('IMLN', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const r = Math.sqrt(re * re + im * im);
        if (r === 0) throw new FormulaError(FormulaErrorType.NUM, 'IMLN: undefined for zero');
        return formatComplex(Math.log(r), Math.atan2(im, re), suffix);
    });

    registry.register('IMLOG2', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const r = Math.sqrt(re * re + im * im);
        if (r === 0) throw new FormulaError(FormulaErrorType.NUM, 'IMLOG2: undefined for zero');
        const ln2 = Math.LN2;
        return formatComplex(Math.log(r) / ln2, Math.atan2(im, re) / ln2, suffix);
    });

    registry.register('IMLOG10', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const r = Math.sqrt(re * re + im * im);
        if (r === 0) throw new FormulaError(FormulaErrorType.NUM, 'IMLOG10: undefined for zero');
        const ln10 = Math.LN10;
        return formatComplex(Math.log(r) / ln10, Math.atan2(im, re) / ln10, suffix);
    });

    registry.register('IMPOWER', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const n = toNum(args[1]);
        // z^n = r^n * (cos(n*theta) + i*sin(n*theta))
        const r = Math.sqrt(re * re + im * im);
        if (r === 0) {
            if (n === 0) return formatComplex(1, 0, suffix);
            return formatComplex(0, 0, suffix);
        }
        const theta = Math.atan2(im, re);
        const rn = Math.pow(r, n);
        return formatComplex(rn * Math.cos(n * theta), rn * Math.sin(n * theta), suffix);
    });

    registry.register('IMPRODUCT', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'IMPRODUCT: requires at least one argument');
        const first = parseComplex(toStr(args[0]));
        let acc: Complex = { re: first.re, im: first.im, suffix: first.suffix };
        for (let i = 1; i < args.length; i++) {
            const b = parseComplex(toStr(args[i]));
            acc = complexMul(acc, b);
        }
        return formatComplex(acc.re, acc.im, acc.suffix);
    });

    registry.register('IMSEC', (...args: FormulaValue[]) => {
        // sec(z) = 1/cos(z)
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const cosRe = Math.cos(re) * Math.cosh(im);
        const cosIm = -Math.sin(re) * Math.sinh(im);
        const denom = cosRe * cosRe + cosIm * cosIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMSEC: undefined');
        return formatComplex(cosRe / denom, -cosIm / denom, suffix);
    });

    registry.register('IMSECH', (...args: FormulaValue[]) => {
        // sech(z) = 1/cosh(z)
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const coshRe = Math.cosh(re) * Math.cos(im);
        const coshIm = Math.sinh(re) * Math.sin(im);
        const denom = coshRe * coshRe + coshIm * coshIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMSECH: undefined');
        return formatComplex(coshRe / denom, -coshIm / denom, suffix);
    });

    registry.register('IMSIN', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // sin(a+bi) = sin(a)*cosh(b) + i*cos(a)*sinh(b)
        return formatComplex(
            Math.sin(re) * Math.cosh(im),
            Math.cos(re) * Math.sinh(im),
            suffix,
        );
    });

    registry.register('IMSINH', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // sinh(a+bi) = sinh(a)*cos(b) + i*cosh(a)*sin(b)
        return formatComplex(
            Math.sinh(re) * Math.cos(im),
            Math.cosh(re) * Math.sin(im),
            suffix,
        );
    });

    registry.register('IMSQRT', (...args: FormulaValue[]) => {
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        // sqrt(a+bi) via polar form
        const r = Math.sqrt(re * re + im * im);
        const theta = Math.atan2(im, re);
        const sqrtR = Math.sqrt(r);
        return formatComplex(
            sqrtR * Math.cos(theta / 2),
            sqrtR * Math.sin(theta / 2),
            suffix,
        );
    });

    registry.register('IMSUB', (...args: FormulaValue[]) => {
        const a = parseComplex(toStr(args[0]));
        const b = parseComplex(toStr(args[1]));
        return formatComplex(a.re - b.re, a.im - b.im, a.suffix);
    });

    registry.register('IMSUM', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'IMSUM: requires at least one argument');
        let reAcc = 0;
        let imAcc = 0;
        let sfx = 'i';
        for (let i = 0; i < args.length; i++) {
            const c = parseComplex(toStr(args[i]));
            reAcc += c.re;
            imAcc += c.im;
            if (i === 0) sfx = c.suffix;
        }
        return formatComplex(reAcc, imAcc, sfx);
    });

    registry.register('IMTAN', (...args: FormulaValue[]) => {
        // tan(z) = sin(z)/cos(z)
        const { re, im, suffix } = parseComplex(toStr(args[0]));
        const sinRe = Math.sin(re) * Math.cosh(im);
        const sinIm = Math.cos(re) * Math.sinh(im);
        const cosRe = Math.cos(re) * Math.cosh(im);
        const cosIm = -Math.sin(re) * Math.sinh(im);
        const denom = cosRe * cosRe + cosIm * cosIm;
        if (denom === 0) throw new FormulaError(FormulaErrorType.DIV_BY_ZERO, 'IMTAN: undefined');
        return formatComplex(
            (sinRe * cosRe + sinIm * cosIm) / denom,
            (sinIm * cosRe - sinRe * cosIm) / denom,
            suffix,
        );
    });
}
